import { LngLat, Map as MlMap, Point } from "maplibre-gl";

type Limit = { value?: string | number; unit?: string; reference?: string };
type Row = { badge: string; limits: string; lower: string; upper: string; floor: number };
type XY = [number, number];
type Anchor = { boundary: XY; tangent: XY; inward: XY };
const anchorsByMap = new WeakMap<MlMap, globalThis.Map<string, Anchor>>();
type LabelBounds = { left: number; top: number; right: number; bottom: number };
const boundaryOverlap = 0;
const curvedAnchorsByMap = new WeakMap<MlMap, globalThis.Map<string, number>>();

// ponytail: sea level, not terrain. With 3D terrain on, `map.project` looks up
// the terrain height of every ring vertex again on each rebuild, and that froze
// the map for 2-4 s after each move. The label position on the map stays
// correct; only the pixel checks (edge margin, overlap, on screen) are off by
// the terrain height. Upgrade path, if a pitched mountain view shows it: look
// up each vertex height once, cache it, project with it.
// maplibre-gl 6 moved the transform behind the internal `_camera`; the public
// `project`/`unproject` always pass the terrain.
function toScreen(map: MlMap, p: XY): XY {
  const q = map._camera.transform.locationToScreenPoint(new LngLat(p[0], p[1]));
  return [q.x, q.y];
}
function toLngLat(map: MlMap, p: XY): LngLat {
  return map._camera.transform.screenPointToLocation(new Point(p[0], p[1]));
}

export function formatAirspaceLimit(limit?: Limit, referenceLabels = false): string {
  if (!limit || limit.value === undefined || limit.value === null || String(limit.value).trim() === "") return "";
  const raw = String(limit.value).trim().toUpperCase();
  if (["GND", "SFC", "UNL"].includes(raw)) return raw;
  const n = Number(raw),
    unit = (limit.unit || "").toUpperCase(),
    ref = (limit.reference || "").toUpperCase();
  if (!Number.isFinite(n) || n < 0) return "";
  if (unit === "FL") return `FL ${n}`;
  if (!["FT", "M"].includes(unit)) return "";
  if (n === 0 && ref === "HEI") return "GND";
  return `${n}${unit === "M" ? " m" : ""}${ref === "HEI" ? (referenceLabels ? " GND" : " AGL") : ref === "ALT" && referenceLabels ? " MSL" : ref === "STD" ? " STD" : ""}`;
}

export function airspaceLabelRow(properties: any): Row | undefined {
  const classCode = String(properties.airspaceClass || "")
    .trim()
    .toUpperCase();
  const badge = /^[A-G]$/.test(classCode) ? classCode : "";
  const references = properties.label?.referenceLabels === true;
  let lower = formatAirspaceLimit(properties.lowerLimit, references);
  const upper = formatAirspaceLimit(properties.upperLimit, references),
    minimum = formatAirspaceLimit(properties.minimumLimit, references);
  if (minimum && minimum !== lower) {
    if (properties.lowerLimit?.reference === "ALT" && !lower.endsWith(" MSL")) lower += " MSL";
    lower += ` / ${minimum}`;
  }
  if (!lower || !upper) return undefined;
  const unit = properties.lowerLimit.unit?.toUpperCase();
  const floor = ["GND", "SFC"].includes(lower)
    ? 0
    : Number(properties.lowerLimit.value) * (unit === "FL" ? 100 : unit === "M" ? 3.28084 : 1);
  return { badge, limits: `${lower} \u2013 ${upper}`, lower, upper, floor };
}

// Equal rings have equal keys regardless of starting vertex or winding.
export function airspaceFootprintKey(rings: number[][][]): string {
  return rings
    .map((ring) => {
      const points = ring.slice();
      if (JSON.stringify(points[0]) === JSON.stringify(points[points.length - 1])) points.pop();
      const tokens = points.map((point) => JSON.stringify(point));
      if (!tokens.length) return "";
      const candidates: string[] = [];
      for (const sequence of [tokens, [...tokens].reverse()]) {
        const first = [...sequence].sort()[0];
        sequence.forEach((token, i) => {
          if (token === first) candidates.push([...sequence.slice(i), ...sequence.slice(0, i)].join(";"));
        });
      }
      return candidates.sort()[0];
    })
    .sort()
    .join("|");
}

function inside(point: XY, ring: XY[]): boolean {
  let result = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i],
      b = ring[j];
    if (a[1] > point[1] !== b[1] > point[1] && point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0])
      result = !result;
  }
  return result;
}
function clip(a: XY, b: XY, width: number, height: number): [XY, XY] | undefined {
  const dx = b[0] - a[0],
    dy = b[1] - a[1];
  let lo = 0,
    hi = 1;
  const ps = [-dx, dx, -dy, dy],
    qs = [a[0], width - a[0], a[1], height - a[1]];
  for (let i = 0; i < 4; i++) {
    if (ps[i] === 0) {
      if (qs[i] < 0) return;
      continue;
    }
    const t = qs[i] / ps[i];
    if (ps[i] < 0) lo = Math.max(lo, t);
    else hi = Math.min(hi, t);
    if (lo > hi) return;
  }
  return [
    [a[0] + dx * lo, a[1] + dy * lo],
    [a[0] + dx * hi, a[1] + dy * hi],
  ];
}

export function placeAirspaceLabel(
  rings: XY[][],
  width: number,
  height: number,
  viewWidth: number,
  viewHeight: number,
  occupied: LabelBounds[] = [],
  anchor?: Anchor
): { point: XY; angle: number; inwardDown: boolean; bounds: LabelBounds; anchor: Anchor } | undefined {
  const outer = rings[0];
  if (!outer?.length) return;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of outer) {
    minX = Math.min(minX, p[0]);
    maxX = Math.max(maxX, p[0]);
    minY = Math.min(minY, p[1]);
    maxY = Math.max(maxY, p[1]);
  }
  if (maxX < 0 || maxY < 0 || minX > viewWidth || minY > viewHeight) return;
  const stride = Math.max(1, Math.ceil((outer.length - 1) / 96));
  const candidates: { a: XY; b: XY; length: number; curved?: boolean }[] = [];
  // Join short, nearly collinear edges so densely sampled OFM boundaries
  // can carry labels too. Reject chords that cut across a pronounced bend.
  for (let i = 0; !anchor && i < outer.length - 1; i += stride) {
    for (let j = i + 1; j < Math.min(outer.length, i + 33); j++) {
      const a = outer[i],
        b = outer[j],
        dx = b[0] - a[0],
        dy = b[1] - a[1],
        length = Math.hypot(dx, dy);
      if (!length) continue;
      if (outer.slice(i + 1, j).some((p) => Math.abs(dx * (p[1] - a[1]) - dy * (p[0] - a[0])) / length > 4)) break;
      const segment = clip(a, b, viewWidth, viewHeight);
      if (segment)
        candidates.push({
          a: segment[0],
          b: segment[1],
          length: Math.hypot(segment[1][0] - segment[0][0], segment[1][1] - segment[0][1]),
        });
    }
  }
  // Smooth, densely sampled curves need a tangent anchor, not a long chord.
  if (!anchor && outer.length >= 16) {
    const count = outer.length - 1;
    for (let i = 0; i < count; i += Math.max(1, Math.ceil(count / 48))) {
      const prev = outer[(i + count - 1) % count],
        p = outer[i],
        next = outer[(i + 1) % count];
      if (p[0] < -height || p[0] > viewWidth + height || p[1] < -height || p[1] > viewHeight + height) continue;
      const ax = p[0] - prev[0],
        ay = p[1] - prev[1],
        bx = next[0] - p[0],
        by = next[1] - p[1];
      const la = Math.hypot(ax, ay),
        lb = Math.hypot(bx, by);
      if (!la || !lb || (ax * bx + ay * by) / (la * lb) < 0.97) continue;
      const dx = next[0] - prev[0],
        dy = next[1] - prev[1],
        len = Math.hypot(dx, dy);
      if (!len) continue;
      candidates.push({
        a: [p[0] - (dx / len) * width, p[1] - (dy / len) * width],
        b: [p[0] + (dx / len) * width, p[1] + (dy / len) * width],
        length: width * 2,
        curved: true,
      });
    }
  }
  const contained = (point: XY) => inside(point, outer) && !rings.slice(1).some((ring) => inside(point, ring));
  if (anchor) {
    const dx = anchor.tangent[0] - anchor.boundary[0],
      dy = anchor.tangent[1] - anchor.boundary[1],
      len = Math.hypot(dx, dy);
    if (!len) return;
    const reach = width;
    candidates.splice(0, candidates.length, {
      a: [anchor.boundary[0] - (dx / len) * reach, anchor.boundary[1] - (dy / len) * reach],
      b: [anchor.boundary[0] + (dx / len) * reach, anchor.boundary[1] + (dy / len) * reach],
      length: reach * 2,
    });
  }
  for (const { a, b, length, curved } of candidates.sort((a, b) => b.length - a.length).slice(0, 64)) {
    if (length < width + 12) continue;
    let angle = Math.atan2(b[1] - a[1], b[0] - a[0]);
    if (angle > Math.PI / 2) angle -= Math.PI;
    if (angle < -Math.PI / 2) angle += Math.PI;
    const u: XY = [Math.cos(angle), Math.sin(angle)],
      v: XY = [-u[1], u[0]];
    for (const fraction of anchor || curved ? [0.5] : [0.5, 0.3, 0.7])
      for (const sign of [1, -1]) {
        if (
          anchor &&
          ((anchor.inward[0] - anchor.boundary[0]) * v[0] + (anchor.inward[1] - anchor.boundary[1]) * v[1]) * sign <= 0
        )
          continue;
        const point: XY = [
          a[0] + (b[0] - a[0]) * fraction + v[0] * sign * (height / 2 - boundaryOverlap),
          a[1] + (b[1] - a[1]) * fraction + v[1] * sign * (height / 2 - boundaryOverlap),
        ];
        const checks: XY[] = [point];
        for (const x of [-width / 2 + 0.1, 0, width / 2 - 0.1])
          for (const y of [-height / 2 + 0.1, 0, height / 2 - 0.1])
            checks.push([point[0] + u[0] * x + v[0] * y, point[1] + u[1] * x + v[1] * y]);
        const bounds = {
          left: Math.min(...checks.map((p) => p[0])) - 6,
          top: Math.min(...checks.map((p) => p[1])) - 6,
          right: Math.max(...checks.map((p) => p[0])) + 6,
          bottom: Math.max(...checks.map((p) => p[1])) + 6,
        };
        if (
          occupied.some(
            (b) => bounds.left < b.right && bounds.right > b.left && bounds.top < b.bottom && bounds.bottom > b.top
          )
        )
          continue;
        // Keep the box inside, with its outer edge exactly on the boundary.
        const innerChecks = checks.filter(
          (p) =>
            (p[0] - point[0]) * v[0] * sign + (p[1] - point[1]) * v[1] * sign >= -height / 2 + boundaryOverlap + 0.1
        );
        if (
          checks.every((p) => p[0] >= 0 && p[0] <= viewWidth && p[1] >= 0 && p[1] <= viewHeight) &&
          innerChecks.every((p) => contained(p))
        )
          return {
            point,
            angle: (angle * 180) / Math.PI,
            inwardDown: sign === 1,
            bounds,
            anchor: {
              boundary: [a[0] + (b[0] - a[0]) * fraction, a[1] + (b[1] - a[1]) * fraction],
              tangent: [a[0] + (b[0] - a[0]) * fraction + u[0] * 10, a[1] + (b[1] - a[1]) * fraction + u[1] * 10],
              inward: [
                a[0] + (b[0] - a[0]) * fraction + v[0] * sign * 10,
                a[1] + (b[1] - a[1]) * fraction + v[1] * sign * 10,
              ],
            },
          };
      }
  }
  return;
}

const LABEL_IMAGE_PREFIX = "airspace-label-translucent:";
// The label images that the previous build used, per map. Its data can stay on screen until the
// new data is applied, so those images are kept for one more build.
const previousLabelImages = new WeakMap<MlMap, Set<string>>();

/**
 * Removes the airspace label images that neither this build nor the previous one uses. Each
 * label stack is a bitmap of 0.3-0.7 MB in MapLibre's image manager (native memory, not JS heap),
 * and nothing else removes one, so a long flight past new airspaces filled the web view.
 */
export function pruneLabelImages(map: MlMap, fc: GeoJSON.FeatureCollection): void {
  const used = new Set<string>();
  for (const f of fc.features) if (typeof f.properties?.icon === "string") used.add(f.properties.icon);
  const previous = previousLabelImages.get(map);
  for (const id of map.listImages()) {
    if (id.startsWith(LABEL_IMAGE_PREFIX) && !used.has(id) && !previous?.has(id)) map.removeImage(id);
  }
  previousLabelImages.set(map, used);
}

export function buildAirspaceLabels(map: MlMap, features: any[]): GeoJSON.FeatureCollection {
  const anchors = anchorsByMap.get(map) || new globalThis.Map<string, Anchor>();
  anchorsByMap.set(map, anchors);
  const groups = new globalThis.Map<string, { rings: number[][][]; rows: Row[]; style: any }>();
  for (const feature of features) {
    const p = feature.properties;
    if (p?._objType !== 0) continue;
    const row = airspaceLabelRow(p);
    if (!row) continue;
    const polygons =
      feature.geometry.type === "Polygon"
        ? [feature.geometry.coordinates]
        : feature.geometry.type === "MultiPolygon"
          ? feature.geometry.coordinates
          : [];
    for (const rings of polygons) {
      // Stack all classes on a shared footprint; retain the reference in each label.
      const key = airspaceFootprintKey(rings);
      const group = groups.get(key) || { rings, rows: [], style: p.label || {} };
      if (!group.rows.some((r) => r.badge === row.badge && r.limits === row.limits)) group.rows.push(row);
      groups.set(key, group);
    }
  }
  const output: GeoJSON.Feature[] = [];
  const canvas = document.createElement("canvas"),
    ctx = canvas.getContext("2d");
  if (!ctx) return { type: "FeatureCollection", features: [] };

  const occupied: LabelBounds[] = [];
  for (const [groupKey, { rings, rows, style }] of [...groups.entries()].sort(
    (a, b) => b[1].rows.length - a[1].rows.length
  )) {
    rows.sort((a, b) => a.floor - b.floor);
    const fontSize = Math.max(10, Math.min(40, Number(style.fontSize) || 20));
    const classSize = Math.max(10, Math.min(40, Number(style.classFontSize) || 27));
    const hasBadge = rows.some((row) => !!row.badge);
    const badgeSize = Math.max(classSize + 6, Number(style.classBoxSize) || classSize + 7);
    const padding = Math.max(2, Number(style.padding) || 6),
      rowGap = Math.max(0, Number(style.rowGap) || 4);
    const rowHeight = Math.max(fontSize * 2 + 12, classSize + 8) + rowGap;
    const family = style.fontFamily || "Arial",
      weight = style.fontWeight || "normal";
    const font = `${weight} ${fontSize}px ${family}, Arial, sans-serif`;
    ctx.font = font;
    const width =
      Math.ceil(
        Math.max(...rows.map((row) => Math.max(ctx.measureText(row.lower).width, ctx.measureText(row.upper).width)))
      ) + (hasBadge ? badgeSize + 32 : 16);
    const height = rows.length * rowHeight + padding * 2;
    const projected: XY[][] = rings.map((ring) => ring.map((p) => toScreen(map, [p[0], p[1]])));
    const curved = curvedAirspaceLabels(map, projected, rows, style, groupKey);
    if (curved !== undefined) {
      output.push(...curved);
      continue;
    }
    const prefix = groupKey + "#";
    const savedKeys = [...anchors.keys()].filter((key) => key.startsWith(prefix));
    const nextIndex = savedKeys.reduce((max, key) => Math.max(max, Number(key.slice(prefix.length)) + 1), 0);
    const candidateKeys = [...savedKeys, prefix + nextIndex];
    let visibleCopies = 0;
    const localOccupied: LabelBounds[] = [];
    for (const anchorKey of candidateKeys) {
      if (visibleCopies >= 1) break;
      const saved = anchors.get(anchorKey);
      const project = (p: XY): XY => toScreen(map, p);
      const fixed = saved
        ? { boundary: project(saved.boundary), tangent: project(saved.tangent), inward: project(saved.inward) }
        : undefined;
      const placement = placeAirspaceLabel(
        projected,
        width,
        height,
        map.getCanvas().clientWidth,
        map.getCanvas().clientHeight,
        [...occupied, ...localOccupied],
        fixed
      );
      if (!placement) continue;
      occupied.push(placement.bounds);
      visibleCopies++;
      // Reserve extra room between repetitions of this same airspace label.
      localOccupied.push({
        left: placement.bounds.left - 120,
        right: placement.bounds.right + 120,
        top: placement.bounds.top - 120,
        bottom: placement.bounds.bottom + 120,
      });
      if (!saved) {
        const unproject = (p: XY): XY => {
          const q = toLngLat(map, p);
          return [q.lng, q.lat];
        };
        anchors.set(anchorKey, {
          boundary: unproject(placement.anchor.boundary),
          tangent: unproject(placement.anchor.tangent),
          inward: unproject(placement.anchor.inward),
        });
      }
      // Read bottom-to-top by altitude, independent of which boundary side faces inward.
      const ordered = [...rows].reverse();
      const id = LABEL_IMAGE_PREFIX + JSON.stringify([ordered, style]);
      if (!map.hasImage(id)) {
        canvas.width = width * 2;
        canvas.height = height * 2;
        ctx.setTransform(2, 0, 0, 2, 0, 0);
        ctx.font = font;
        ctx.textBaseline = "middle";
        ctx.fillStyle = style.backgroundColor || "rgba(255,255,255,0.55)";
        ctx.fillRect(0, 0, width, height);
        const border = Math.max(0, Number(style.borderWidth ?? 1));
        ctx.strokeStyle = style.borderColor || "#000";
        ctx.lineWidth = border;
        if (border) ctx.strokeRect(border / 2, border / 2, width - border, height - border);
        ordered.forEach((row, i) => {
          const y = padding + i * rowHeight,
            textLeft = hasBadge ? badgeSize + 24 : 8,
            textRight = width - 8,
            mid = y + rowHeight / 2;
          if (row.badge) {
            ctx.fillStyle = style.classBackgroundColor || "#000";
            ctx.fillRect(9, mid - badgeSize / 2, badgeSize, badgeSize);
            ctx.fillStyle = style.classTextColor || "#fff";
            ctx.textAlign = "center";
            ctx.font = `${style.classFontWeight || "bold"} ${classSize}px ${family}, Arial, sans-serif`;
            ctx.fillText(row.badge, 9 + badgeSize / 2, mid);
          }
          ctx.font = font;
          ctx.textAlign = "center";
          ctx.fillStyle = style.textColor || "#000";
          ctx.fillText(row.upper, (textLeft + textRight) / 2, mid - fontSize / 2 - 4);
          ctx.fillText(row.lower, (textLeft + textRight) / 2, mid + fontSize / 2 + 4);
          ctx.strokeStyle = style.textColor || "#000";
          ctx.beginPath();
          ctx.moveTo(textLeft, mid);
          ctx.lineTo(textRight, mid);
          ctx.lineWidth = 1;
          ctx.stroke();
        });
        map.addImage(id, ctx.getImageData(0, 0, canvas.width, canvas.height), { pixelRatio: 2 });
      }
      const ll = toLngLat(map, placement.point);
      output.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [ll.lng, ll.lat] },
        properties: { icon: id, rotation: placement.angle, priority: -rows.length * 1000000 + rows[0].floor },
      });
    }
  }
  const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: output };
  pruneLabelImages(map, fc);
  return fc;
}

/** A bounded circularity check keeps curved labels out of straight/irregular polygons. */
export function circularBoundary(rings: XY[][]): { center: XY; radius: number } | undefined {
  if (rings.length !== 1 || rings[0].length < 24) return;
  const ring = rings[0];
  let left = Infinity,
    right = -Infinity,
    top = Infinity,
    bottom = -Infinity;
  for (const p of ring) {
    left = Math.min(left, p[0]);
    right = Math.max(right, p[0]);
    top = Math.min(top, p[1]);
    bottom = Math.max(bottom, p[1]);
  }
  const center: XY = [(left + right) / 2, (top + bottom) / 2],
    radius = (right - left + bottom - top) / 4;
  if (radius < 1 || Math.abs(right - left - (bottom - top)) > radius * 0.12) return;
  const step = Math.max(1, Math.floor((ring.length - 1) / 32));
  for (let i = 0; i < ring.length - 1; i += step)
    if (Math.abs(Math.hypot(ring[i][0] - center[0], ring[i][1] - center[1]) - radius) > radius * 0.04) return;
  return { center, radius };
}

function curvedAirspaceLabels(
  map: MlMap,
  rings: XY[][],
  rows: Row[],
  style: any,
  key: string
): GeoJSON.Feature[] | undefined {
  const circle = circularBoundary(rings);
  if (!circle) return undefined;
  const ring = rings[0],
    count = ring.length - 1,
    canvas = map.getCanvas();
  const fontSize = Math.max(10, Math.min(40, Number(style.fontSize) || 20));
  const requiredWidth = Math.max(
    ...rows.map((row) => row.limits.length * fontSize * 0.6 + (row.badge ? fontSize * 1.8 : 0))
  );
  const innerRadius = circle.radius - fontSize * (rows.length + 0.5);
  if (innerRadius <= 0 || (innerRadius * Math.PI) / 2 < requiredWidth + 20) return [];
  const anchors = curvedAnchorsByMap.get(map) || new globalThis.Map<string, number>();
  curvedAnchorsByMap.set(map, anchors);
  const existing = anchors.get(key);
  const choices =
    existing === undefined ? Array.from({ length: 8 }, (_, i) => Math.floor((i * count) / 8)) : [existing];
  const half = Math.floor(count / 8);
  for (const middle of choices) {
    // Use at most 33 points in a fixed quarter-circle, anchored to source vertices.
    const arc: XY[] = Array.from(
      { length: 33 },
      (_, i) => ring[(middle - half + Math.round((i * 2 * half) / 32) + count) % count]
    );
    if (!arc.every((p) => p[0] > 12 && p[0] < canvas.clientWidth - 12 && p[1] > 12 && p[1] < canvas.clientHeight - 12))
      continue;
    anchors.set(key, middle);
    const output: GeoJSON.Feature[] = [];
    rows.forEach((row, i) => {
      const inset = fontSize * (i + 0.8),
        factor = (circle.radius - inset) / circle.radius;
      const points = arc.map(
        (p) =>
          [
            circle.center[0] + (p[0] - circle.center[0]) * factor,
            circle.center[1] + (p[1] - circle.center[1]) * factor,
          ] as XY
      );
      if (points[points.length - 1][0] < points[0][0]) points.reverse();
      const coordinates = points.map((p) => {
        const ll = toLngLat(map, p);
        return [ll.lng, ll.lat];
      });
      let badgeIcon = "";
      if (row.badge) {
        badgeIcon = "curved-class:" + JSON.stringify([row.badge, style.classBackgroundColor, style.classTextColor]);
        if (!map.hasImage(badgeIcon)) {
          const c = document.createElement("canvas");
          c.width = 48;
          c.height = 48;
          const ctx = c.getContext("2d");
          if (ctx) {
            ctx.fillStyle = style.classBackgroundColor || "#000";
            ctx.fillRect(0, 0, 48, 48);
            ctx.fillStyle = style.classTextColor || "#fff";
            ctx.font = "bold 34px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(row.badge, 24, 25);
            map.addImage(badgeIcon, ctx.getImageData(0, 0, 48, 48), { pixelRatio: 2 });
          }
        }
      }
      output.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates },
        properties: {
          curvedAirspaceLabel: true,
          limits: row.limits,
          badgeIcon,
          fontSize,
          textColor: style.textColor || "#000",
          backgroundColor: style.backgroundColor || "rgba(255,255,255,0.7)",
          priority: -rows.length * 1000000 + row.floor,
        },
      });
    });
    return output;
  }
  return [];
}
